'use client';

import Link from 'next/link';
import { useCallback, useEffect, useMemo, useState, type FormEvent, type KeyboardEvent } from 'react';
import { useAsk, type Exchange } from '@/ai/useAsk';
import { localePath, type Lang } from '@/i18n/config';
import type { LiveSnapshot } from '@/lab/types';
import LiveBadge from '@/components/ui/LiveBadge';
import s from '@/app/[lang]/anu/anu-live.module.css';

const copy = {
  en: {
    status: 'ANU observer signal',
    run: 'Current run',
    tick: 'Tick',
    agents: 'Agents',
    links: 'Links',
    noRun: 'Observer connected. No active ANU run is being reported.',
    runReady: 'The observer returned a run. The badge shows the freshness the site can prove.',
    promptTitle: 'Ask the laboratory',
    promptLead: 'This conversation uses the server-side MWS relay. The prompt includes the latest observer snapshot when one exists.',
    placeholder: 'Ask about ANU, MAGIC, the observer signal or the next shared experiment',
    send: 'Send',
    stop: 'Stop',
    clear: 'Clear',
    empty: 'Start with a question. The answer streams from the configured model through the lab gateway.',
    pending: 'Thinking',
    error: 'The model gateway returned an error.',
    observerReason: 'Reason',
    openRoom: 'Move this work into ATRIUM',
    suggestions: [
      'What can the current ANU evidence prove?',
      'Sketch a safe experiment for the next ANU run.',
      'How should humans and agents share one ATRIUM room?',
    ],
  },
  ru: {
    status: 'Сигнал обсервера ANU',
    run: 'Текущий запуск',
    tick: 'Тик',
    agents: 'Агенты',
    links: 'Связи',
    noRun: 'Обсервер подключён. Активный запуск ANU не сообщается.',
    runReady: 'Обсервер вернул запуск. Бейдж показывает свежесть, которую сайт может доказать.',
    promptTitle: 'Спросить лабораторию',
    promptLead: 'Диалог идёт через серверный ретранслятор MWS. В запрос попадает последний снимок обсервера, если он существует.',
    placeholder: 'Спроси про ANU, MAGIC, сигнал обсервера или следующий общий эксперимент',
    send: 'Отправить',
    stop: 'Остановить',
    clear: 'Очистить',
    empty: 'Начни с вопроса. Ответ поступает из настроенной модели через шлюз лаборатории.',
    pending: 'Модель думает',
    error: 'Шлюз модели вернул ошибку.',
    observerReason: 'Причина',
    openRoom: 'Перенести работу в ATRIUM',
    suggestions: [
      'Что доказывают текущие свидетельства ANU?',
      'Набросай безопасный эксперимент для следующего запуска ANU.',
      'Как людям и агентам работать в одной комнате ATRIUM?',
    ],
  },
} as const;

function metric(value: number | null, empty: string): string {
  return value === null ? empty : String(value);
}

function exchangeLabel(exchange: Exchange, t: (typeof copy)[Lang]): string {
  if (exchange.status === 'pending' || exchange.status === 'streaming') return t.pending;
  if (exchange.status === 'error') return exchange.error || t.error;
  return '';
}

export default function ANULivePlayground({
  lang,
  initialSnapshot,
  modelName,
  mwsReady,
}: {
  lang: Lang;
  initialSnapshot: LiveSnapshot;
  modelName: string | null;
  mwsReady: boolean;
}) {
  const t = copy[lang];
  const [snapshot, setSnapshot] = useState(initialSnapshot);
  const [draft, setDraft] = useState('');
  const [polling, setPolling] = useState(false);
  const onActions = useCallback(() => undefined, []);
  const assistant = useAsk(onActions);

  const refresh = useCallback(async () => {
    if (document.hidden) return;
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), 8000);
    setPolling(true);
    try {
      const response = await fetch('/api/lab/agents', { cache: 'no-store', signal: controller.signal });
      if (!response.ok) return;
      const next = (await response.json()) as LiveSnapshot;
      if (next?.source === 'anu-observer' && typeof next.freshness === 'string') setSnapshot(next);
    } catch {
      // The last verified snapshot remains visible. A transport miss is not
      // converted into a false LIVE or OFFLINE claim on the client.
    } finally {
      window.clearTimeout(timeout);
      setPolling(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
    const timer = window.setInterval(() => void refresh(), 7000);
    const resume = () => { if (!document.hidden) void refresh(); };
    document.addEventListener('visibilitychange', resume);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', resume);
    };
  }, [refresh]);

  const statusCopy = snapshot.runId ? t.runReady : t.noRun;
  const stats = snapshot.stats;
  const emptyMetric = lang === 'ru' ? 'не установлено' : 'not established';
  const exchanges = useMemo(() => assistant.exchanges, [assistant.exchanges]);

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (!mwsReady || assistant.busy || !draft.trim()) return;
    const question = draft.trim();
    setDraft('');
    void assistant.ask(question);
  };

  const onKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      event.currentTarget.form?.requestSubmit();
    }
  };

  return (
    <>
      <section className={s.signalGrid} aria-labelledby="anu-signal-title">
        <div className={`panel panel--solid ${s.signalLead}`}>
          <div className={s.signalHead}>
            <div>
              <p className="label">{t.status}</p>
              <h2 id="anu-signal-title">{snapshot.source === 'anu-observer' ? 'ANU / OBSERVER' : 'ANU / UNKNOWN'}</h2>
            </div>
            <LiveBadge lang={lang} freshness={snapshot.freshness} reason={snapshot.reason} mode={snapshot.mode} />
          </div>
          <p className={s.signalNote} aria-live="polite">{statusCopy}</p>
          {snapshot.reason && (
            <p className="machine machine--sm">{t.observerReason}: {snapshot.reason}</p>
          )}
          {polling && <span className={`machine machine--sm ${s.polling}`}>SYNCING</span>}
        </div>
        <div className={`panel ${s.metricTile}`}>
          <span className="label">{t.run}</span>
          <strong>{snapshot.runId ? snapshot.runId.slice(0, 12) : emptyMetric}</strong>
        </div>
        <div className={`panel ${s.metricTile}`}>
          <span className="label">{t.tick}</span>
          <strong>{metric(snapshot.tick, emptyMetric)}</strong>
        </div>
        <div className={`panel ${s.metricTile}`}>
          <span className="label">{t.agents}</span>
          <strong>{metric(stats?.activeAgents ?? null, emptyMetric)}</strong>
        </div>
        <div className={`panel ${s.metricTile}`}>
          <span className="label">{t.links}</span>
          <strong>{metric(stats?.links ?? null, emptyMetric)}</strong>
        </div>
      </section>

      <section className={s.playgroundGrid} aria-labelledby="anu-playground-title">
        <div className={`panel panel--solid ${s.promptPanel}`}>
          <div className={s.promptHeader}>
            <div>
              <p className="label">MWS / {modelName || 'managed model'}</p>
              <h2 id="anu-playground-title">{t.promptTitle}</h2>
            </div>
            {exchanges.length > 0 && (
              <button type="button" className={s.textButton} onClick={assistant.reset}>{t.clear}</button>
            )}
          </div>
          <p className={s.promptLead}>{t.promptLead}</p>
          {exchanges.length === 0 ? (
            <div className={s.emptyConversation}>
              <p>{t.empty}</p>
              <div className={s.suggestions}>
                {t.suggestions.map((suggestion) => (
                  <button key={suggestion} type="button" onClick={() => setDraft(suggestion)}>{suggestion}</button>
                ))}
              </div>
            </div>
          ) : (
            <div className={s.transcript} aria-live="polite">
              {exchanges.map((exchange) => (
                <article key={exchange.id} className={s.exchange}>
                  <p className="label">YOU</p>
                  <p className={s.question}>{exchange.question}</p>
                  {(exchange.answer || exchange.status === 'pending') && (
                    <>
                      <p className={`label ${s.answerLabel}`}>LAB {exchangeLabel(exchange, t)}</p>
                      <p className={s.answer}>{exchange.answer || '…'}</p>
                    </>
                  )}
                </article>
              ))}
            </div>
          )}
          <form className={s.composer} onSubmit={submit}>
            <textarea
              value={draft}
              onChange={(event) => setDraft(event.target.value)}
              onKeyDown={onKeyDown}
              placeholder={mwsReady ? t.placeholder : t.error}
              disabled={!mwsReady || assistant.busy}
              maxLength={2000}
              rows={2}
              aria-label={t.promptTitle}
            />
            <button type="submit" className="btn btn--primary" disabled={!mwsReady || assistant.busy || !draft.trim()}>
              {assistant.busy ? t.stop : t.send}
            </button>
          </form>
          {assistant.busy && (
            <button type="button" className={s.stopButton} onClick={assistant.cancel}>{t.stop}</button>
          )}
          <Link className={s.roomLink} href={localePath(lang, '/command')}>
            {t.openRoom} <span aria-hidden="true">↗</span>
          </Link>
        </div>
      </section>
    </>
  );
}
